import { subDays } from 'date-fns';
import { validationTypes } from 'components/FormCreator/types';
import { DataType } from 'components/BasicTable/types';

export enum beneficiarySteps {
	BENEFICIARY = 'beneficiary',
	BANK_ACCOUNT = 'bank_account',
	REVIEW = 'review',
	COMPLETE = 'complete',
}

export enum beneficiaryStepsStatus {
	notStarted = 'notStarted',
	inProgress = 'inProgress',
	pending = 'pending',
	error = 'error',
	completed = 'completed',
}

export enum BeneficiaryStatus {
	None = 'None',
	PendingBankAccountDetails = 'PendingBankAccountDetails',
	PendingSigning = 'PendingSigning',
	SignedPendingBOApproval = 'SignedPendingBOApproval',
	Approved = 'Approved',
	ApprovedWithRisk = 'ApprovedWithRisk',
	Declined = 'Declined',
	Suspended = 'Suspended',
	Deleted = 'Deleted',
	DeclinedForRepair = 'DeclinedForRepair',
}

export enum BeneficiaryStatusTitles {
	None = 'None',
	PendingBankAccountDetails = 'Pending Bank Account Details',
	PendingSigning = 'Awaiting signature',
	SignedPendingBOApproval = 'Pending Approval',
	Approved = 'Approved',
	ApprovedWithRisk = 'Approved',
	Declined = 'Declined',
	Suspended = 'Suspended',
	Deleted = 'Deleted',
	DeclinedForRepair = 'Declined For Repair',
}

export const PendingBeneficiaryStatus = ['PendingBankAccountDetails', 'PendingSigning', 'SignedPendingBOApproval'];

export enum beneficiaryType {
	COMPANY = 'Company',
	INDIVIDUAL = 'Individual',
}

export enum paymentsMainTypes {
	REGULAR = 'regular',
	PRIORITY = 'priority',
}

export enum paymentsTypes {
	REGULAR = 'regular',
	PRIORITY_ABA = 'priority_aba',
	PRIORITY_BIC = 'priority_bic',
}

export enum paymentsMethodsMapping {
	acct_number = 'acctNumber',
	iban = 'iban',
	bic_swift = 'bicSwift',
	aba = 'aba',
	bank_code = 'bankCode',
	sort_code = 'sortCode',
	bsb_code = 'bsbCode',
	branch_code = 'branchCode',
	clabe = 'clabe',
	cnaps = 'cnaps',
	ifsc = 'ifsc',
	beneficiary_postcode = 'postcode',
	beneficiary_state_or_province = 'stateOrProvince',
}
export enum paymentsMethodsBeneficiaryProfile {
	accountNumber = 'acct_number',
	iban = 'iban',
	bicSwift = 'bic_swift',
	aba = 'aba',
	bankCode = 'bank_code',
	sortCode = 'sort_code',
	bsbCode = 'bsb_code',
	branchCode = 'branch_code',
	clabe = 'clabe',
	cnaps = 'cnaps',
	ifsc = 'ifsc',
	postcode = 'postcode',
	stateOrProvince = 'state_or_province',
}

export type paymentsMethods = {
	[paymentsMethodsMapping.acct_number]?: string;
	[paymentsMethodsMapping.bic_swift]?: string;
	[paymentsMethodsMapping.iban]?: string;
	[paymentsMethodsMapping.aba]?: string;
	[paymentsMethodsMapping.bank_code]?: string;
	[paymentsMethodsMapping.sort_code]?: string;
	[paymentsMethodsMapping.bsb_code]?: string;
	[paymentsMethodsMapping.branch_code]?: string;
	[paymentsMethodsMapping.clabe]?: string;
	[paymentsMethodsMapping.cnaps]?: string;
	[paymentsMethodsMapping.ifsc]?: string;
	[paymentsMethodsMapping.beneficiary_postcode]?: string;
	[paymentsMethodsMapping.beneficiary_state_or_province]?: string;
};

export const paymentsMethodsList: string[] = [
	'acct_number',
	'iban',
	'bic_swift',
	'aba',
	'bank_code',
	'sort_code',
	'bsb_code',
	'branch_code',
	'clabe',
	'cnaps',
	'ifsc',
	'beneficiary_postcode',
	'beneficiary_state_or_province',
];

export const paymentsMethodsDisplayList: paymentsMethodsMapping[] = [
	paymentsMethodsMapping.acct_number,
	paymentsMethodsMapping.bic_swift,
	paymentsMethodsMapping.iban,
	paymentsMethodsMapping.aba,
	paymentsMethodsMapping.bank_code,
	paymentsMethodsMapping.sort_code,
	paymentsMethodsMapping.bsb_code,
	paymentsMethodsMapping.branch_code,
	paymentsMethodsMapping.clabe,
	paymentsMethodsMapping.cnaps,
	paymentsMethodsMapping.ifsc,
	paymentsMethodsMapping.beneficiary_postcode,
	paymentsMethodsMapping.beneficiary_state_or_province,
];

export type paymentsMethodType = {
	[key: string]: { optional: boolean; fields: string[]; type: string };
};

export type PaymentCategoryOption = {
	id: string;
	label: string;
	primary: string;
	secondary?: string;
	tertiary?: string;
};

export type localPaymentType = {
	bankAccountAccountHolderName?: string;
	iban?: string;
	address?: string;
	city?: string;
	country?: string;
};
export interface priorityPaymentType extends localPaymentType {
	bic: string;
}

export interface BeneficiaryDetails {
	type?: beneficiaryType;
	companyName?: string;
	firstName?: string;
	lastName?: string;
	nickname?: string;
	identificationNumber?: number;
	countryId?: string;
	address?: string;
	city?: string;
	files?: object[];
	formalBankApproveFile?: object;
	comment?: string;
}

export interface Beneficiary extends BeneficiaryDetails, paymentsMethods {
	id?: number;
	companyId?: number;
	bankAccountHolderName?: string;
	bankAccountCountryId?: string;
	currencyId?: string;
	isLocalPaymentAllowed?: boolean;
	isPriorityPaymentAllowed?: boolean;
	payments: [paymentsTypes] | [];
	accountNumber?: string;
	paymentsOptions: paymentsMethodType;
	priority?: paymentsMethods;
	regular?: paymentsMethods;
	createdAt?: string;
	updatedAt?: string;
	wcStatus: BeneficiaryStatus;
	beneficiaryId?: string;
	companyName?: string;
	firstName?: string;
	lastName?: string;
	status?: BeneficiaryStatus;
	isSigned: boolean;
	actions?: string;
	reference?: string;
	paymentsList: Payment[];
	note?: string;
	comment?: string;
	files?: any[];
	fileId?: number;
	foreignTradeAgreement?: boolean;
	backupBeneficiaryId?: number | null;
	formalBankApproveFile?: any;
	formalBankApproveFileId?: number;
	website?: string;
	subStatus?: string;
	commentForUnequalCountries?: string;
	userEditComment?: string;
	paymentCategories?: string[] | PaymentCategoryOption[];
}

export const beneficiaryIntialState: Beneficiary = {
	type: beneficiaryType.COMPANY,
	payments: [],
	paymentsOptions: {},
	isLocalPaymentAllowed: false,
	isPriorityPaymentAllowed: false,
	wcStatus: BeneficiaryStatus.Declined,
	isSigned: false,
	paymentsList: [],
	note: '',
	files: [],
	paymentCategories: [],
};

export const BeneficiaryDetailsCompany: (keyof BeneficiaryDetails)[] = [
	'type',
	'companyName',
	'nickname',
	'identificationNumber',
	'countryId',
	'city',
	'address',
];

export const BeneficiaryDetailsCompanyForDisplay: (keyof BeneficiaryDetails)[] = [
	...BeneficiaryDetailsCompany,
	'files',
	'formalBankApproveFile',
	'comment',
];

export const BeneficiaryDetailsLocal: (keyof BeneficiaryDetails)[] = [
	'type',
	'firstName',
	'lastName',
	'nickname',
	'identificationNumber',
	'countryId',
	'city',
	'address',
];

export const BeneficiaryDetailsBankAccount: (keyof Beneficiary)[] = [
	'bankAccountHolderName',
	'bankAccountCountryId',
	'commentForUnequalCountries',
	'currencyId',
];

export type ValidateBankAccount = {
	beneficiaryId: number;
	currencyId: number;
	bankAccountCountryId: number;
	priority?: paymentsMethods[];
	regular?: paymentsMethods[];
	bankAccountHolderName: string;
	fileId?: number;
	comment?: string;
	foreignTradeAgreement: boolean;
	commentForUnequalCountries?: string;
	isSigned?: boolean;
	paymentCategories?: number[];
};

export const BeneficiaryTableColumns: {
	[key in keyof Partial<Beneficiary>]: DataType;
} = {
	companyName: 'custom',
	countryId: 'string',
	payments: 'custom',
	updatedAt: 'date',
	status: 'string',
	subStatus: 'custom',
	reference: 'string',
};

export type BeneficiaryRequest = {
	referenceNumber: string;
	date: string;
	companyName: string;
	currency: number;
	country: number;
	type: string;
};

export const BeneficiaryRequestTableColumns: {
	[key: string]: DataType;
} = {
	referenceNumber: 'link',
	date: 'date',
	companyName: 'string',
	currency: 'string',
	country: 'string',
	type: 'string',
	//createdBy: 'string',
};

export const BeneficiaryId: keyof Beneficiary = 'id';

export const BeneficiaryDefaultSort: keyof Beneficiary = 'updatedAt';
export const BeneficiaryProfileDefaultSort: keyof Beneficiary = 'createdAt';

//*********  Payment : move to payment constants *************/
export type Payment = {
	createdAt: string;
	dueDate: string;
	amount: number;
	type: paymentsTypes;
	status: string;
	fee: number;
	reasonTitle: string;
	reference: string;
};

export const PaymentTableColumn: { [key in keyof Partial<Payment>]: DataType } = {
	reference: 'string',
	createdAt: 'date',
	dueDate: 'date',
	amount: 'custom',
	status: 'custom',
	type: 'string',
	fee: 'number',
	reasonTitle: 'string',
};

export const fieldsValidations: {
	[key in keyof Partial<Beneficiary>]: validationTypes[];
} = {
	companyName: [validationTypes.requierd, validationTypes.englishLettersSpecialChars],
	lastName: [validationTypes.requierd],
	firstName: [validationTypes.requierd],
	identificationNumber: [validationTypes.requierd, validationTypes.englishAndDigits],
	countryId: [validationTypes.requierd],
	nickname: [validationTypes.requierd, validationTypes.englishLettersSpecialChars],
	currencyId: [validationTypes.requierd],
	bankAccountHolderName: [validationTypes.requierd, validationTypes.min3Chars],
	bankAccountCountryId: [validationTypes.requierd],
	city: [validationTypes.requierd, validationTypes.lettersOnly, validationTypes.cityName],
	address: [validationTypes.requierd, validationTypes.englishAndDigits],
	aba: [validationTypes.requierd],
	acctNumber: [validationTypes.requierd, validationTypes.min5Chars],
	bicSwift: [validationTypes.requierd],
	iban: [validationTypes.requierd],
	files: [validationTypes.requierd],
	foreignTradeAgreement: [validationTypes.checked],
	website: [validationTypes.website],
	paymentCategories: [validationTypes.requierd],
};

export const formalBankApproveFileValidation = {
	formalBankApproveFile: [validationTypes.requierd],
};

export const commentForUnequalCountriesValidation: { [key: string]: validationTypes[] | undefined } = {
	commentForUnequalCountries: [validationTypes.requierd, validationTypes.min3Chars],
};
